import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostBinding, HostListener, Inject, Input, NgZone, OnChanges, OnDestroy, Optional, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { LogStreamMessage, LogStreamService } from '@portal-core/live-log/services/log-stream.service';
import { UserSettings } from '@portal-core/users/interfaces/user-settings';
import { USER_SETTINGS_SERVICE } from '@portal-core/users/tokens/user-settings-service.token';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { BehaviorSubject, Subscription } from 'rxjs';

dayjs.extend(utc);

const TIMESTAMP_FORMAT = 'HH:mm:ss.SSS';

const SETTINGS = {
  showIcons: 'live-log:show-icons',
  theme: 'live-log:theme',
  timestampMode: 'live-log:timestamp-mode',
} as const;

type LogSeverity = 'info' | 'warn' | 'error' | 'debug';
export type TimestampMode = 'none' | 'local' | 'utc';
export type TerminalTheme = 'dark' | 'light';

// Distinguishes regular log output (`log`) from process state events (`event`) like
// "Build started: HTML5", "Compile complete: …". Both render in the same terminal flow but use
// different visual treatment so users can scan past state markers when looking for output.
type LogLineKind = 'log' | 'event';

// Caller-supplied resolver that maps a log line's `src` (and optional line number) onto a
// router-link command array (or null to render the source label as plain text). Each feature
// using the live log knows how to address its own files (build → license/project/branch path,
// publication → wherever publication source files live, …) so the component itself stays
// neutral.
export type LiveLogSourceLinkResolver = (src: string, srcLine?: number) => any[] | null;

// Leading glyph rendered before each line to convey type/severity without relying on color
// alone. Using monochrome Unicode symbols (not emoji) keeps the terminal aesthetic clean and
// avoids font-rendering surprises across platforms.
//
// Events get their own marker (▸) so they're distinguishable from info-severity log lines —
// otherwise both would share the same icon, since events always carry "info" severity from the
// writer.
const ICON_FOR_EVENT = '▸';
const ICON_FOR_SEVERITY: Record<LogSeverity, string> = {
  error: '✖',
  warn: '⚠',
  info: '-',
  debug: '⋯'
};

interface LogLine {
  kind: LogLineKind;
  severity: LogSeverity;
  message: string;
  // Pre-computed in both flavors so flipping `timestampMode` doesn't require re-parsing the
  // ISO string on every visible line.
  timestampUtc: string;
  timestampLocal: string;
  // The link's inner text — `Content/foo.htm:42` or `Content/foo.htm`, no brackets/space.
  // The leading space and surrounding `[ ]` are rendered as plain text in the template so
  // only the path itself is clickable.
  sourceLabel: string;
  src?: string;
  srcLine?: number;
}

@Component({
  selector: 'mc-live-log',
  templateUrl: './live-log.component.html',
  styleUrls: ['./live-log.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LiveLogComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() streamUrl: string;
  @Input() sourceLinkResolver?: LiveLogSourceLinkResolver;
  @Input() emptyLabelOverride?: string;
  @Input() loading: boolean = false;
  @Input() generalError?: string;
  @Input() detailedErrors?: string[];

  theme: TerminalTheme = 'light';

  @HostBinding('class.mc-terminal-theme-dark') get isDarkTheme() { return this.theme === 'dark'; }
  @HostBinding('class.mc-terminal-theme-light') get isLightTheme() { return this.theme === 'light'; }

  // BehaviorSubject so *cdkVirtualFor receives an Observable<LogLine[]> and is notified on
  // each emission. A plain array passed to *cdkVirtualFor only feeds CDK once at subscribe
  // time; push() mutations are never seen by CDK's internal differ.
  readonly linesSubject = new BehaviorSubject<LogLine[]>([]);
  get lines(): LogLine[] { return this.linesSubject.value; }

  isComplete = false;
  timestampMode: TimestampMode = 'none';
  showIcons = false;

  // --- Find ---
  findOpen = false;
  findTerm = '';
  // Set of line indices that contain at least one match — O(1) lookup per rendered row.
  matchSet = new Set<number>();
  // Segments for each matching line: alternating plain/match text spans, computed once in
  // recomputeMatches() so the template just iterates rather than calling indexOf per render.
  messageParts = new Map<number, Array<{ text: string; isMatch: boolean; occurrenceIndex: number }>>();
  // Each entry is one match occurrence, ordered top-to-bottom then left-to-right.
  private matchIndices: Array<{ lineIndex: number; occurrenceIndex: number }> = [];
  private currentMatchIndex = -1;

  get matchCount(): number { return this.matchIndices.length; }
  get currentMatchPosition(): number { return this.currentMatchIndex >= 0 ? this.currentMatchIndex + 1 : 0; }
  get currentMatchLineIndex(): number {
    return this.currentMatchIndex >= 0 ? this.matchIndices[this.currentMatchIndex].lineIndex : -1;
  }
  get currentMatchOccurrenceIndex(): number {
    return this.currentMatchIndex >= 0 ? this.matchIndices[this.currentMatchIndex].occurrenceIndex : -1;
  }

  // itemSize passed to cdkVirtualFor — must match the rendered height of .mc-live-log-line
  // exactly: font-size (12px) × line-height (1.5) = 18px. No margin/padding on the line
  // element. If either value changes in SCSS this constant must be updated to match.
  readonly LINE_HEIGHT_PX = 18;

  iconFor(line: LogLine): string {
    return line.kind === 'event' ? ICON_FOR_EVENT : ICON_FOR_SEVERITY[line.severity];
  }

  @ViewChild('findInput') private findInputRef: ElementRef<HTMLInputElement>;
  @ViewChild(CdkVirtualScrollViewport, { static: true }) private viewport: CdkVirtualScrollViewport;

  @HostListener('keydown.control.f', ['$event'])
  onCtrlF(event: KeyboardEvent) {
    event.preventDefault();
    this.openFind();
  }

  @HostListener('keydown.escape', ['$event'])
  onEscape(event: KeyboardEvent) {
    if (this.findOpen) {
      event.stopPropagation();
      this.closeFind();
    }
  }

  // The SSE source is owned by `LogStreamService` and shared with any sibling consumer
  // subscribing to the same URL (e.g. a logs-grid component in another tab of the same dialog).
  // EventSource isn't patched by Zone.js, so subscribe callbacks need NgZone.run() to trigger
  // change detection.
  private logStreamSubscription: Subscription;
  // Tracks whether the viewport is scrolled to (or near) the bottom. Auto-scroll to new lines
  // only fires when true; the user can scroll up to read history without being dragged back.
  private atBottom = true;
  private readonly onViewportScroll = () => {
    const el = this.viewport.elementRef.nativeElement;
    const distanceFromBottom = el.scrollHeight - el.clientHeight - el.scrollTop;
    this.atBottom = distanceFromBottom < 4;
  };

  // Lines buffered since the last flush; scheduleFlush() coalesces a frame's worth of arrivals into one CD pass and one scroll-to-bottom.
  private pendingLines: LogLine[] = [];
  private flushHandle: number | null = null;

  constructor(
    private logStreamService: LogStreamService,
    private changeDetectorRef: ChangeDetectorRef,
    private ngZone: NgZone,
    @Optional() @Inject(USER_SETTINGS_SERVICE) private userSettingsService: UserSettings | null
  ) { }

  ngAfterViewInit() {
    // Listen for scroll events outside Angular's zone (no need to trigger CD on every pixel).
    // Update atBottom so the auto-scroll gate stays accurate.
    this.ngZone.runOutsideAngular(() => {
      this.viewport.elementRef.nativeElement.addEventListener('scroll', this.onViewportScroll);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.streamUrl && this.streamUrl) {
      const prev = changes.streamUrl.previousValue as string | undefined;
      if (!prev || prev !== this.streamUrl) {
        this.openStream();
      }
    }
  }

  private openStream() {
    this.logStreamSubscription?.unsubscribe();
    this.cancelPendingFlush();
    this.linesSubject.next([]);
    this.isComplete = false;
    this.atBottom = true;

    const savedTimestampMode = this.userSettingsService?.getSettingByName<TimestampMode>(SETTINGS.timestampMode);
    if (savedTimestampMode === 'none' || savedTimestampMode === 'local' || savedTimestampMode === 'utc') {
      this.timestampMode = savedTimestampMode;
    }
    const savedTheme = this.userSettingsService?.getSettingByName<TerminalTheme>(SETTINGS.theme);
    if (savedTheme === 'dark' || savedTheme === 'light') {
      this.theme = savedTheme;
    }
    const savedShowIcons = this.userSettingsService?.getSettingByName<boolean>(SETTINGS.showIcons);
    if (savedShowIcons !== undefined) { this.showIcons = savedShowIcons; }

    // Subscribe to the multicast stream service. Joining a stream that already has a replay
    // buffer (e.g. the user opened a sibling tab first and is now coming back to Log) replays
    // every prior message into our `next` handler synchronously, rebuilding `lines` to the
    // same point the sibling is at.
    this.logStreamSubscription = this.logStreamService.getStream$(this.streamUrl).subscribe({
      next: msg => this.handleStreamMessage(msg),
      complete: () => this.ngZone.run(() => {
        this.flushPendingLines();
        this.isComplete = true;
        this.changeDetectorRef.markForCheck();
      })
    });
  }

  private handleStreamMessage(msg: LogStreamMessage) {
    if (msg.kind === 'open') {
      return;
    }
    if (msg.kind === 'done') {
      // Flush synchronously (not via rAF) so any buffered lines render before isComplete flips.
      this.ngZone.run(() => {
        this.flushPendingLines();
        this.isComplete = true;
        this.changeDetectorRef.markForCheck();
      });
      return;
    }
    const line = msg.kind === 'raw' ? this.lineFromRawText(msg.text) : this.lineFromEnvelope(msg);
    if (!line) {
      return;
    }
    this.pendingLines.push(line);
    this.scheduleFlush();
  }

  // Coalesces a frame's worth of lines into one CD pass; wrapped in ngZone.run() since handleStreamMessage (and the rAF it schedules) runs outside Angular's zone.
  private scheduleFlush() {
    if (this.flushHandle !== null) {
      return;
    }
    this.flushHandle = requestAnimationFrame(() => {
      this.flushHandle = null;
      this.ngZone.run(() => {
        this.flushPendingLines();
        this.changeDetectorRef.markForCheck();
      });
    });
  }

  // Moves buffered lines into linesSubject, updates find-matches, and scrolls to bottom if pinned; caller handles markForCheck().
  private flushPendingLines() {
    if (this.pendingLines.length === 0) {
      return;
    }
    if (this.flushHandle !== null) {
      cancelAnimationFrame(this.flushHandle);
      this.flushHandle = null;
    }
    const arr = this.linesSubject.value;
    const startIndex = arr.length;
    arr.push(...this.pendingLines);
    this.pendingLines = [];
    this.linesSubject.next(arr);
    if (this.findTerm) {
      for (let i = startIndex; i < arr.length; i++) {
        this.appendMatchForLine(i);
      }
    }
    if (this.atBottom) {
      // Defer one tick so Angular renders the new items before CDK scrolls to them.
      setTimeout(() => this.viewport?.scrollToIndex(this.lines.length - 1));
    }
  }

  private cancelPendingFlush() {
    if (this.flushHandle !== null) {
      cancelAnimationFrame(this.flushHandle);
      this.flushHandle = null;
    }
    this.pendingLines = [];
  }

  ngOnDestroy() {
    this.logStreamSubscription?.unsubscribe();
    this.cancelPendingFlush();
    this.viewport?.elementRef.nativeElement.removeEventListener('scroll', this.onViewportScroll);
  }

  getSourceRouterLink(src: string): any[] | null {
    if (!src || !this.sourceLinkResolver) { return null; }
    return this.sourceLinkResolver(src) ?? null;
  }

  setTimestampMode(mode: TimestampMode) {
    this.timestampMode = mode;
    this.userSettingsService?.setSettingByName$(SETTINGS.timestampMode, mode);
    this.changeDetectorRef.markForCheck();
  }

  setTheme(theme: TerminalTheme) {
    this.theme = theme;
    this.userSettingsService?.setSettingByName$(SETTINGS.theme, theme);
    this.changeDetectorRef.markForCheck();
  }

  setShowIcons(value: boolean) {
    if (value === this.showIcons) { return; }
    this.showIcons = value;
    this.userSettingsService?.setSettingByName$(SETTINGS.showIcons, value);
    this.changeDetectorRef.markForCheck();
  }

  trackLine(index: number): number {
    return index;
  }

  // The CDK virtual scroll viewport only re-measures itself on a browser window resize
  // (CdkVirtualScrollViewport listens to ViewportRuler.change()); a programmatic container
  // resize like MatDialogRef.updateSize() doesn't trigger that, so callers that resize this
  // component's container (e.g. the dialog maximize button) must call this afterward.
  checkViewportSize() {
    this.viewport?.checkViewportSize();
  }

  // Focuses the terminal surface so keydown.control.f (host-scoped, see onCtrlF) actually
  // reaches this instance. Callers should invoke this whenever the Log tab becomes active,
  // since the user hasn't necessarily clicked into the terminal yet.
  focus() {
    this.viewport?.elementRef.nativeElement.focus();
  }

  openFind() {
    this.findOpen = true;
    this.changeDetectorRef.markForCheck();
    setTimeout(() => this.findInputRef?.nativeElement.focus());
  }

  closeFind() {
    this.findOpen = false;
    this.findTerm = '';
    this.matchIndices = [];
    this.matchSet = new Set();
    this.messageParts = new Map();
    this.currentMatchIndex = -1;
    this.changeDetectorRef.markForCheck();
  }

  onFindInput(term: string) {
    this.findTerm = term;
    this.recomputeMatches();
    if (this.matchIndices.length > 0) {
      this.scrollToCurrentMatch();
    }
  }

  nextMatch() {
    if (!this.matchIndices.length) return;
    this.currentMatchIndex = (this.currentMatchIndex + 1) % this.matchIndices.length;
    this.scrollToCurrentMatch();
    this.changeDetectorRef.markForCheck();
  }

  prevMatch() {
    if (!this.matchIndices.length) return;
    this.currentMatchIndex = (this.currentMatchIndex - 1 + this.matchIndices.length) % this.matchIndices.length;
    this.scrollToCurrentMatch();
    this.changeDetectorRef.markForCheck();
  }

  private recomputeMatches() {
    if (!this.findTerm) {
      this.matchIndices = [];
      this.matchSet = new Set();
      this.messageParts = new Map();
      this.currentMatchIndex = -1;
      this.changeDetectorRef.markForCheck();
      return;
    }
    const lower = this.findTerm.toLowerCase();
    const occurrences: Array<{ lineIndex: number; occurrenceIndex: number }> = [];
    const matchedLines = new Set<number>();
    const parts = new Map<number, Array<{ text: string; isMatch: boolean; occurrenceIndex: number }>>();
    const lines = this.lines;

    for (let i = 0; i < lines.length; i++) {
      const segments = this.segmentsForLine(lines[i].message, lower);
      if (segments) {
        segments.filter(s => s.isMatch).forEach(s => occurrences.push({ lineIndex: i, occurrenceIndex: s.occurrenceIndex }));
        matchedLines.add(i);
        parts.set(i, segments);
      }
    }

    this.matchIndices = occurrences;
    this.matchSet = matchedLines;
    this.messageParts = parts;
    this.currentMatchIndex = occurrences.length > 0 ? 0 : -1;
    this.changeDetectorRef.markForCheck();
  }

  private appendMatchForLine(lineIndex: number) {
    const segments = this.segmentsForLine(this.lines[lineIndex].message, this.findTerm.toLowerCase());
    if (!segments) return;
    segments.filter(s => s.isMatch).forEach(s => this.matchIndices.push({ lineIndex, occurrenceIndex: s.occurrenceIndex }));
    this.matchSet.add(lineIndex);
    this.messageParts.set(lineIndex, segments);
    this.changeDetectorRef.markForCheck();
  }

  // Splits a message into plain/match segments for the given lowercased term.
  // Returns null when the message contains no match — avoids allocating a segments array.
  private segmentsForLine(
    msg: string,
    lower: string
  ): Array<{ text: string; isMatch: boolean; occurrenceIndex: number }> | null {
    const msgLower = msg.toLowerCase();
    const segments: Array<{ text: string; isMatch: boolean; occurrenceIndex: number }> = [];
    let searchStart = 0;
    let occurrenceIndex = 0;

    while (true) {
      const idx = msgLower.indexOf(lower, searchStart);
      if (idx === -1) {
        if (searchStart < msg.length) {
          segments.push({ text: msg.substring(searchStart), isMatch: false, occurrenceIndex: 0 });
        }
        break;
      }
      if (idx > searchStart) {
        segments.push({ text: msg.substring(searchStart, idx), isMatch: false, occurrenceIndex: 0 });
      }
      segments.push({ text: msg.substring(idx, idx + this.findTerm.length), isMatch: true, occurrenceIndex });
      occurrenceIndex++;
      searchStart = idx + this.findTerm.length;
    }

    return occurrenceIndex > 0 ? segments : null;
  }

  private scrollToCurrentMatch() {
    if (this.currentMatchIndex < 0 || !this.viewport) return;
    const lineIndex = this.matchIndices[this.currentMatchIndex].lineIndex;
    const itemTop = lineIndex * this.LINE_HEIGHT_PX;
    const centered = itemTop - (this.viewport.getViewportSize() - this.LINE_HEIGHT_PX) / 2;
    this.viewport.scrollToOffset(Math.max(0, centered));
  }

  // Map an envelope from the shared stream service onto a renderable terminal line.
  //
  // - `log` envelopes carry compiler/runtime output (`{sev, msg, src, srcLine, ...}`) and
  //   render with a severity-driven color and the source-file annotation when present.
  // - `event` envelopes are process state markers (BuildStart, CompileComplete, …) and render
  //   with the muted `event` style. Events without a textual message (setProgress,
  //   incProgress) carry no surface-level info and are filtered out by returning `null`.
  private lineFromEnvelope(msg: { kind: 'log' | 'event'; data: { sev?: string; msg?: string; t?: string; src?: string; srcLine?: number } }): LogLine | null {
    const data = msg.data;
    const kind: LogLineKind = msg.kind;
    const message = (data.msg ?? '').replace(/\s+$/, '');

    if (kind === 'event' && !message) {
      return null;
    }

    const src = kind === 'log' && data.src ? data.src.replace(/\\/g, '/') : undefined;

    return {
      kind,
      severity: this.normalizeSeverity(data.sev),
      message,
      timestampUtc: this.formatTimestampUtc(data.t),
      timestampLocal: this.formatTimestampLocal(data.t),
      sourceLabel: this.formatSourceLabel(src, data.srcLine),
      src,
      srcLine: data.srcLine,
    };
  }

  // Fallback for an SSE line the upstream couldn't parse as JSON (madbuild occasionally
  // emits a raw stdout line before the structured envelope writer takes over). Render it as
  // an info-severity log with no metadata so nothing's silently dropped.
  private lineFromRawText(text: string): LogLine {
    return { kind: 'log', severity: 'info', message: text.replace(/\s+$/, ''), timestampUtc: '', timestampLocal: '', sourceLabel: '' };
  }

  private formatSourceLabel(src: string | undefined, srcLine: number | undefined): string {
    if (!src) { return ''; }
    return srcLine ? `${src}:${srcLine}` : src;
  }

  private normalizeSeverity(sev: string | undefined): LogSeverity {
    switch (sev) {
      case 'error': return 'error';
      case 'warn': return 'warn';
      case 'debug': return 'debug';
      default: return 'info';
    }
  }

  private formatTimestampUtc(iso: string | undefined): string {
    if (!iso) return '';
    const date = dayjs.utc(iso);
    return date.isValid() ? date.format(TIMESTAMP_FORMAT) : '';
  }

  private formatTimestampLocal(iso: string | undefined): string {
    if (!iso) return '';
    const date = dayjs(iso);
    return date.isValid() ? date.format(TIMESTAMP_FORMAT) : '';
  }
}
